import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../utils/helpers/hydration';

/**
 * Asks component for SignNow displaying article cards with links
 *
 * @product signnow
 * @source asks
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Asks component for displaying linked article cards about eSignature topics
 * @constant ASKS_HEADING - "The ins and outs of eSignature" - Main heading for asks section
 * @constant ASKS_DESCRIPTION - "Find everything you need to know about electronic signatures. We’ve collected all the best tips and step-by-step instructions for working with eSignatures in a single place." - Description text for section
 */
@Component({
  tag: 'sn-asks',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNAsks {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * HTML tag for the main heading element (h1, h2, h3, h4, h5, h6)
   * @default "h4"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h4';

  /**
   * List of article items to display
   * @dynamic
   * @type {array}
   * @.type {object}
   * @.property {string} href - URL for the article link
   * @.property {string} title - Article card title
   * @.property {string} description - Article card description text
   * @.required href
   * @.required title
   * @.required description
   * @minItems 3
   * @maxItems 3
   */
  @Prop({ mutable: true }) items:
    | Array<{
        href: string;
        title: string;
        description: string;
      }>
    | string = [];

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
    this.items = parseJsonProp(this.items, []);
  }

  render() {
    // Ensure items is an array
    if (!Array.isArray(this.items) || this.items.length === 0) {
      return null;
    }

    const cardsCount = this.items.length;
    let cardsModifier = '';
    if (cardsCount === 3) cardsModifier = 'sn-asks__cards--triple';

    return (
      <me-section class="sn-asks" yGap="2xl" yGapLg="5xl" role="region" aria-label="Features">
        <me-row direction="column" yGap="2xs" yGapLg="3xl">
          <me-column span="12">
            <me-heading align="center" variant="3" tag={this.headingTag}>
              {getConstant(this.constants, 'ASKS_HEADING', 'The ins and outs of eSignature')}
            </me-heading>
            <me-text align="center">
              {getConstant(this.constants, 'ASKS_DESCRIPTION', 'Find everything you need to know about electronic signatures. We’ve collected all the best tips and step-by-step instructions for working with eSignatures in a single place.')}
            </me-text>
          </me-column>
          <me-column
            span="12"
            class={`sn-asks__cards${cardsModifier ? ' ' + cardsModifier : ''}`}
            itemScope
            itemType="https://schema.org/ItemList"
          >
            {this.items.map((item, index) => {
              // Each card gets its corresponding image: frame-7, frame-8, frame-9
              const imageNumber = index + 7;
              const imageName = `frame-${imageNumber}`;
              const imageBase = `https://marketing-static.signnow.com/349/images/e-signature/${imageName}`;

              return (
                <a href={item.href} key={index} class="sn-asks__link">
                  <features-card-type-6
                    itemscope
                    itemtype="https://schema.org/ListItem"
                    itemprop="itemListElement"
                  >
                    <meta itemprop="position" content={String(index + 1)} />
                    <me-picture
                      slot="icon"
                      srcSet={`${imageBase}.png 1x, ${imageBase}@2x.png 2x`}
                      webpSrcSet={`${imageBase}.webp 1x, ${imageBase}@2x.webp 2x`}
                      aspectRatio="16/9"
                      alt={imageName}
                    ></me-picture>
                    <me-heading slot="heading" variant="5">
                      {item.title}
                    </me-heading>
                    <me-text slot="description">{item.description}</me-text>
                  </features-card-type-6>
                </a>
              );
            })}
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
